import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Star, Download, Shield, Zap, ArrowRight, Key, Lock } from 'lucide-react';

const industryColors = {
  'Construction': 'badge-construction',
  'Healthcare': 'badge-healthcare',
  'CRM': 'badge-crm',
  'Finance': 'badge-finance',
  'Logistics': 'badge-logistics',
  'Communication': 'badge-communication',
  'Developer Tools': 'badge-developer',
  'Marketing': 'badge-marketing',
  'E-commerce': 'badge-ecommerce',
  'Human Resources': 'badge-hr',
  'Data & BI': 'badge-data',
  'AI & ML': 'badge-ai',
  'Productivity': 'badge-productivity',
  'Automation': 'badge-automation',
  'DevOps': 'badge-devops',
  'Security': 'badge-security',
};

const authIcons = {
  'OAuth2': <Lock className="w-3 h-3" />,
  'API Key': <Key className="w-3 h-3" />,
  'Bearer': <Key className="w-3 h-3" />,
};

const ConnectorCard = ({ connector }) => {
  const { 
    id, 
    name, 
    service_name, 
    description, 
    industry, 
    category, 
    authentication_method, 
    is_verified, 
    stability, 
    deployment_count, 
    rating, 
    icon_url 
  } = connector;

  return (
    <div
      className="connector-card flex flex-col h-full bg-[#0B1120] border border-white/5 hover:border-primary/30 rounded-xl overflow-hidden group"
      data-testid={`connector-card-${service_name?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Main Content - Clickable */}
      <Link to={`/connector/${id}`} className="flex flex-col flex-1 p-6">
        {/* Header with larger logo */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-secondary to-secondary/50 flex items-center justify-center overflow-hidden flex-shrink-0 border border-white/5">
            {icon_url ? (
              <img 
                src={icon_url} 
                alt={name} 
                className="w-12 h-12 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`w-12 h-12 items-center justify-center ${icon_url ? 'hidden' : 'flex'}`}>
              <Zap className="w-8 h-8 text-primary" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-heading font-semibold text-lg text-white truncate group-hover:text-primary transition-colors">
                {service_name || name}
              </h3>
              {is_verified && (
                <div className="flex items-center gap-1 bg-accent/10 px-2 py-0.5 rounded-full">
                  <Shield className="w-3 h-3 text-accent" />
                  <span className="text-[10px] text-accent font-medium">Verified</span>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-500">{category}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-slate-400 leading-relaxed mb-4 flex-1 line-clamp-2">
          {description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${industryColors[industry] || 'bg-slate-800/50 text-slate-300 border-slate-700/50'}`}>
            {industry}
          </span>
          <Badge variant="outline" className="text-xs border-slate-700/50 text-slate-400 flex items-center gap-1.5 px-2.5 py-1">
            {authIcons[authentication_method] || <Key className="w-3 h-3" />}
            {authentication_method}
          </Badge>
          {stability === 'stable' && (
            <Badge variant="outline" className="text-xs border-emerald-900/50 text-emerald-400 bg-emerald-950/30 px-2.5 py-1">
              Stable
            </Badge>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 pt-4 border-t border-white/5">
          <div className="flex items-center gap-1.5 text-sm">
            <Download className="w-4 h-4 text-slate-500" />
            <span className="text-slate-300 font-medium">{deployment_count?.toLocaleString() || 0}</span>
            <span className="text-slate-500 text-xs">deploys</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span className="text-slate-300 font-medium">{rating?.toFixed(1) || '0.0'}</span>
          </div>
        </div>
      </Link>

      {/* Action Footer */}
      <div className="px-6 py-4 bg-white/[0.02] border-t border-white/5 flex items-center justify-between">
        <Link 
          to={`/connector/${id}`}
          className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1 group/link"
        >
          View Details
          <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all" />
        </Link>
        <Button
          size="sm"
          className="bg-primary hover:bg-primary/90 text-white px-4 h-8 text-xs font-medium"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = `/connector/${id}?tab=install`;
          }}
          data-testid={`install-btn-${service_name?.toLowerCase().replace(/\s+/g, '-')}`}
        >
          Install
        </Button>
      </div>
    </div>
  );
};

export default ConnectorCard;
